/**
 * Client-side pricing/badge derivation shared by the three public-facing
 * marketplace pages (LandingPage, PublicPropertyBrowsePage,
 * PublicPropertyDetailPage). `Property`/`PropertyListRow` carry no direct
 * price field — price lives on each nested `Unit.rent_amount`, a decimal
 * STRING the backend already quantizes. `Number()` here is for display
 * formatting only; no further arithmetic is performed on the parsed value.
 */
import type { Unit } from '../types/property';

function vacantRents(units: Unit[]): number[] {
  return units
    .filter((unit) => unit.is_vacant)
    .map((unit) => Number(unit.rent_amount))
    .filter((value) => Number.isFinite(value));
}

/** Minimum rent among vacant units — the "From KES X" card price. Null when nothing is vacant. */
export function deriveFromPrice(units: Unit[]): number | null {
  const rents = vacantRents(units);
  return rents.length === 0 ? null : Math.min(...rents);
}

/** Min/max rent span across vacant units, for the detail page header. Null when nothing is vacant. */
export function deriveVacantPriceRange(units: Unit[]): { min: number; max: number } | null {
  const rents = vacantRents(units);
  return rents.length === 0 ? null : { min: Math.min(...rents), max: Math.max(...rents) };
}

/** Min/max bedroom count across vacant units — for the detail page's specs strip. Null when nothing is vacant. */
export function deriveBedroomRange(units: Unit[]): { min: number; max: number } | null {
  const values = units.filter((unit) => unit.is_vacant).map((unit) => unit.bedrooms);
  return values.length === 0 ? null : { min: Math.min(...values), max: Math.max(...values) };
}

/** Min/max bathroom count across vacant units (Unit.bathrooms is a decimal string, e.g. "1.5"). */
export function deriveBathroomRange(units: Unit[]): { min: number; max: number } | null {
  const values = units
    .filter((unit) => unit.is_vacant)
    .map((unit) => Number(unit.bathrooms))
    .filter((value) => Number.isFinite(value));
  return values.length === 0 ? null : { min: Math.min(...values), max: Math.max(...values) };
}

/** Total floor area across vacant units with a recorded size, for the specs strip. Null when none is recorded. */
export function deriveTotalSquareFeet(units: Unit[]): number | null {
  const values = units
    .filter((unit) => unit.is_vacant && unit.square_feet !== null)
    .map((unit) => unit.square_feet as number);
  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0);
}

function formatRange(range: { min: number; max: number }, unit: string): string {
  return range.min === range.max ? `${range.min}${unit}` : `${range.min}–${range.max}${unit}`;
}

export function formatBedroomRange(range: { min: number; max: number }): string {
  return formatRange(range, ' bd');
}

export function formatBathroomRange(range: { min: number; max: number }): string {
  return formatRange(range, ' ba');
}

export function formatKes(amount: number): string {
  return `KES ${amount.toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;
}

/** Compact form for map-pin labels, e.g. 95_000 -> "95K", 1_200_000 -> "1.2M". */
export function formatKesCompact(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(amount % 1_000_000 === 0 ? 0 : 1)}M`;
  if (amount >= 1_000) return `${Math.round(amount / 1_000)}K`;
  return String(Math.round(amount));
}

export function formatPriceRange(range: { min: number; max: number }): string {
  return range.min === range.max
    ? `From ${formatKes(range.min)}`
    : `${formatKes(range.min)} – ${formatKes(range.max)}`;
}

/** Properties created within this window show a "New" badge — a judgment call, not a backend field. */
const NEW_LISTING_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function isRecentlyListed(createdAt: string, now: number = Date.now()): boolean {
  const created = new Date(createdAt).getTime();
  return Number.isFinite(created) && now - created <= NEW_LISTING_WINDOW_MS;
}

/** "3 August 2026" — the detail page's "Listed on" line. No formatter exists elsewhere in this app (every other page renders ISO dates raw); this is deliberately scoped to the marketplace detail page's own polish, not a shared convention change. */
export function formatListedDate(createdAt: string): string | null {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  return new Intl.DateTimeFormat('en-KE', { day: 'numeric', month: 'long', year: 'numeric' }).format(created);
}
